import React, { useState, useMemo } from 'react';
import {
  Download,
  Loader2,
  ImageOff,
  CheckSquare,
  Square,
  Search,
  CheckCircle,
  AlertTriangle,
  X,
  FileArchive,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../ui/dialog';
import { resolveProductField } from '../ProductCard';
import { useExportPolling } from '../../../hooks/useExportPolling';
import { socialPostingService } from '../../../services/socialPosting';
import { downloadBlob } from './exportUtils';
import type { SocialPostProduct, ExportJob } from '../../../services/socialPosting';

type ModalState = 'selection' | 'processing' | 'complete';

interface ExportBatchModalProps {
  open: boolean;
  onClose: () => void;
  products: SocialPostProduct[];
  postId: string;
  designId: string;
  designName: string;
}

export function ExportBatchModal({
  open,
  onClose,
  products,
  postId,
  designId,
  designName,
}: ExportBatchModalProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(products.map((p) => p.id)));
  const [search, setSearch] = useState('');
  const [modalState, setModalState] = useState<ModalState>('selection');
  const [exportJobId, setExportJobId] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [completedJob, setCompletedJob] = useState<ExportJob | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const { job, isPolling, progress } = useExportPolling({
    jobId: modalState === 'processing' ? exportJobId : null,
    onComplete: (j) => {
      setCompletedJob(j);
      setModalState('complete');
    },
    onFailed: (j) => {
      setCompletedJob(j);
      setModalState('complete');
    },
  });

  const filteredProducts = useMemo(() => {
    if (!search.trim()) return products;
    const q = search.toLowerCase();
    return products.filter((p) => {
      const title = resolveProductField(p.raw_data, 'title').toLowerCase();
      const id = resolveProductField(p.raw_data, 'id').toLowerCase();
      return title.includes(q) || id.includes(q);
    });
  }, [products, search]);

  const allSelected = selectedIds.size === products.length;
  const noneSelected = selectedIds.size === 0;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(products.map((p) => p.id)));
    }
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleStartExport = async () => {
    try {
      setIsStarting(true);
      setStartError(null);
      const result = await socialPostingService.startBatchExport(postId, designId, {
        format: 'png',
        quality: 100,
        scale: 2,
        productIds: Array.from(selectedIds),
      });
      setExportJobId(result.jobId);
      setModalState('processing');
    } catch (err) {
      setStartError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsStarting(false);
    }
  };

  const handleDownload = async () => {
    if (!exportJobId) return;
    try {
      setIsDownloading(true);
      setDownloadError(null);
      const blob = await socialPostingService.downloadExportJob(exportJobId);
      downloadBlob(blob, `${designName || 'batch-export'}.zip`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('Download failed:', msg);
      setDownloadError(msg);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCancel = async () => {
    if (exportJobId && modalState === 'processing') {
      try {
        await socialPostingService.cancelExportJob(exportJobId);
      } catch { /* ignore */ }
    }
    resetAndClose();
  };

  const resetAndClose = () => {
    setModalState('selection');
    setExportJobId(null);
    setCompletedJob(null);
    setStartError(null);
    setDownloadError(null);
    setIsStarting(false);
    setIsDownloading(false);
    onClose();
  };

  const canClose = modalState !== 'processing';

  function formatFileSize(bytes: number | null): string {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && canClose) resetAndClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* ── State 1: Selection ────────────────────────── */}
        {modalState === 'selection' && (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl">Export Products</DialogTitle>
              <DialogDescription className="text-sm">
                Select which products to include in the batch export. Each product will generate one image with your design.
              </DialogDescription>
            </DialogHeader>

            {startError && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-destructive/10 text-destructive text-sm">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {startError}
              </div>
            )}

            {/* Search + Select All */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search products..."
                  className="h-10 pl-10 text-sm rounded-xl"
                />
              </div>
              <Button variant="outline" className="rounded-lg h-10 px-4 shrink-0 text-sm" onClick={toggleAll}>
                {allSelected ? 'Deselect All' : 'Select All'}
              </Button>
            </div>

            {/* Product list */}
            <div className="flex-1 overflow-y-auto -mx-6 px-6 min-h-0">
              <div className="space-y-1.5 py-1">
                {filteredProducts.map((product) => {
                  const isChecked = selectedIds.has(product.id);
                  const title = resolveProductField(product.raw_data, 'title') || 'Untitled';
                  const image = resolveProductField(product.raw_data, 'image');
                  const productId = resolveProductField(product.raw_data, 'id');
                  const price = resolveProductField(product.raw_data, 'price');

                  return (
                    <button
                      key={product.id}
                      onClick={() => toggleOne(product.id)}
                      className={`
                        w-full flex items-center gap-4 px-4 py-3 rounded-xl text-left transition-colors
                        ${isChecked ? 'bg-primary/10 border border-primary/30' : 'hover:bg-muted/50 border border-transparent'}
                      `}
                    >
                      {isChecked ? (
                        <CheckSquare className="h-5 w-5 text-primary shrink-0" />
                      ) : (
                        <Square className="h-5 w-5 text-muted-foreground shrink-0" />
                      )}

                      <div className="h-12 w-12 rounded-xl border overflow-hidden bg-muted/30 shrink-0">
                        {image ? (
                          <img src={image} alt="" className="h-full w-full object-cover" loading="lazy" />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center">
                            <ImageOff className="h-5 w-5 text-muted-foreground/40" />
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-base font-medium truncate">{title}</p>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          {productId && <span className="font-mono truncate">{productId}</span>}
                          {price && <span className="tabular-nums">{price}</span>}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-3 border-t">
              <p className="text-sm text-muted-foreground">
                {selectedIds.size} of {products.length} selected
              </p>
              <div className="flex items-center gap-3">
                <Button variant="outline" className="rounded-lg h-10 px-5 text-sm" onClick={resetAndClose}>
                  Cancel
                </Button>
                <Button
                  className="rounded-lg h-10 px-5 text-sm"
                  onClick={handleStartExport}
                  disabled={noneSelected || isStarting}
                >
                  {isStarting ? (
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-1.5" />
                  )}
                  {isStarting ? 'Starting...' : `Export ${selectedIds.size} Product${selectedIds.size !== 1 ? 's' : ''}`}
                </Button>
              </div>
            </div>
          </>
        )}

        {/* ── State 2: Processing ──────────────────────── */}
        {modalState === 'processing' && (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl">Exporting...</DialogTitle>
              <DialogDescription className="text-sm">
                Your images are being rendered in the background. You can wait here or close and check back.
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col items-center justify-center py-12 gap-6">
              <div className="relative h-20 w-20">
                <Loader2 className="h-20 w-20 animate-spin text-primary/30" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-lg font-bold">{progress}%</span>
                </div>
              </div>

              {/* Progress bar */}
              <div className="w-full max-w-md">
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="flex items-center justify-between mt-2 text-sm text-muted-foreground">
                  <span>
                    Rendering product {(job?.completedCount ?? 0) + (job?.failedCount ?? 0)} of {job?.totalCount ?? selectedIds.size}...
                  </span>
                  {(job?.failedCount ?? 0) > 0 && (
                    <span className="text-amber-500">{job?.failedCount} failed</span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-3 border-t">
              <Button variant="outline" className="rounded-lg h-10 px-5 text-sm" onClick={handleCancel}>
                <X className="h-4 w-4 mr-1.5" />
                Cancel
              </Button>
            </div>
          </>
        )}

        {/* ── State 3: Complete ────────────────────────── */}
        {modalState === 'complete' && completedJob && (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl">
                {completedJob.status === 'failed' && completedJob.completedCount === 0
                  ? 'Export Failed'
                  : 'Export Complete'}
              </DialogTitle>
            </DialogHeader>

            <div className="flex flex-col items-center justify-center py-10 gap-4">
              {completedJob.status === 'failed' && completedJob.completedCount === 0 ? (
                <AlertTriangle className="h-16 w-16 text-destructive" />
              ) : (
                <CheckCircle className="h-16 w-16 text-emerald-500" />
              )}

              <div className="text-center space-y-1">
                {completedJob.completedCount > 0 && (
                  <p className="text-lg font-semibold">
                    {completedJob.completedCount} of {completedJob.totalCount} products rendered
                  </p>
                )}
                {completedJob.fileSize && (
                  <p className="text-sm text-muted-foreground flex items-center justify-center gap-1.5">
                    <FileArchive className="h-4 w-4" />
                    ZIP file: {formatFileSize(completedJob.fileSize)}
                  </p>
                )}
              </div>

              {completedJob.failedCount > 0 && (
                <div className="w-full max-w-md px-4 py-3 rounded-xl bg-amber-500/10 text-amber-600 text-sm">
                  <p className="font-medium flex items-center gap-1.5">
                    <AlertTriangle className="h-4 w-4" />
                    {completedJob.failedCount} product{completedJob.failedCount !== 1 ? 's' : ''} failed to render
                  </p>
                  {completedJob.errors.length > 0 && (
                    <ul className="mt-2 space-y-1 text-xs text-amber-500/80 list-disc list-inside">
                      {completedJob.errors.slice(0, 5).map((e, i) => (
                        <li key={i}>{e.productId}: {e.error}</li>
                      ))}
                      {completedJob.errors.length > 5 && (
                        <li>...and {completedJob.errors.length - 5} more</li>
                      )}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {downloadError && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-destructive/10 text-destructive text-sm">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                Download failed: {downloadError}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-3 border-t">
              <Button variant="outline" className="rounded-lg h-10 px-5 text-sm" onClick={resetAndClose}>
                Close
              </Button>
              {completedJob.completedCount > 0 && completedJob.status !== 'failed' && (
                <Button
                  className="rounded-lg h-10 px-5 text-sm"
                  onClick={handleDownload}
                  disabled={isDownloading}
                >
                  {isDownloading ? (
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-1.5" />
                  )}
                  {isDownloading ? 'Downloading...' : 'Download ZIP'}
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
